"use client";
// The stateful, variant-aware action button (loading / success states,
// primary/secondary/tertiary). Composes the lower-level pressable primitive
// from "./components/base-action-button". Brand-blind: all color/sizing
// divergence resolves through --brand-button-* CSS variables (see
// brands/{kinsa,owlet}.css).
import {
  BaseActionButton,
  type ActionButtonEventData,
  type ActionButtonTheme,
} from "./components/base-action-button";
import type { DeepPartial } from "../types";
import type { RegisteredEventName } from "@repo/statsig";
import classNames from "classnames";
import React from "react";
import CheckIcon from "@repo/ui/branded/check-icon";
import { ButtonText } from "../typography";
import { LoadingSpinner } from "../loading-spinner";

export type LoadingStyleConfig =
  | "spinner-left" // Spinner + Text (Spinner on the left side)
  | "spinner-right" // Spinner + Text (Spinner on the right side)
  | "spinner-only"; // Spinner Only (center spinner)

/**
 * Loading spinner component with configurable layout styles
 */
interface LoadingContentProps {
  text: string;
  style: LoadingStyleConfig;
}

const LoadingContent: React.FC<LoadingContentProps> = ({ text, style }) => {
  const spinner = <LoadingSpinner />;

  switch (style) {
    case "spinner-left":
      return (
        <>
          {spinner}
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
        </>
      );
    case "spinner-right":
      return (
        <>
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
          {spinner}
        </>
      );
    case "spinner-only":
      return spinner;
    default:
      return (
        <>
          {spinner}
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
        </>
      );
  }
};

export type SuccessStyleConfig =
  | "check-left" // Check + Text (Check on the left side)
  | "check-right" // Check + Text (Check on the right side)
  | "check-only"; // Check Only (center check)

/**
 * Success checkmark component with configurable layout styles
 */
interface SuccessContentProps {
  text: string;
  style: SuccessStyleConfig;
}

const SuccessContent: React.FC<SuccessContentProps> = ({ text, style }) => {
  // Brand check glyph (resolved per brand via @repo/ui/branded), centered in a
  // fixed 16px box so it sits consistently inline with the button text. Reuses
  // the same CheckIcon as the checkbox rather than a third hardcoded SVG.
  const checkmark = (
    <span
      className="inline-flex size-4 shrink-0 items-center justify-center select-none"
      aria-label="Success"
    >
      <CheckIcon className="text-current" />
    </span>
  );

  switch (style) {
    case "check-left":
      return (
        <>
          {checkmark}
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
        </>
      );
    case "check-right":
      return (
        <>
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
          {checkmark}
        </>
      );
    case "check-only":
      return checkmark;
    default:
      return (
        <>
          {checkmark}
          <ButtonText as="span" className="truncate">
            {text}
          </ButtonText>
        </>
      );
  }
};

export interface ActionButtonProps {
  text: string;
  onPress?: () => void;
  eventData: ActionButtonEventData;
  customEventName?: RegisteredEventName;
  className?: string;
  form?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingStyle?: LoadingStyleConfig;
  success?: boolean;
  successStyle?: SuccessStyleConfig;
  variant?: "primary" | "secondary" | "tertiary";
  type?: "button" | "submit" | "reset";
  theme?: DeepPartial<ActionButtonTheme>;
  /**
   * @deprecated Prefer `leadingIcon` / `trailingIcon` for new code. The
   * `content` prop renders before the text inside the button row and is kept
   * for backwards compatibility.
   */
  content?: React.ReactNode;
  /**
   * Optional icon rendered before the button text. Hidden automatically when
   * the button is in `loading` or `success` state (those states render their
   * own spinner / checkmark).
   */
  leadingIcon?: React.ReactNode;
  /**
   * Optional icon rendered after the button text. Hidden automatically when
   * the button is in `loading` or `success` state (those states render their
   * own spinner / checkmark).
   */
  trailingIcon?: React.ReactNode;
  "data-testid"?: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  text,
  onPress,
  eventData,
  customEventName,
  className,
  form,
  disabled,
  loading = false,
  loadingStyle = "spinner-left",
  success = false,
  successStyle = "check-left",
  variant = "primary",
  type = "button",
  theme,
  content,
  leadingIcon,
  trailingIcon,
  "data-testid": dataTestId,
}) => {
  // Button variant class definitions. Color/state values resolve per-brand
  // via --brand-button-* CSS variables (see brands/{kinsa,owlet}.css), so the
  // component itself stays brand-agnostic.
  const buttonVariantClasses = {
    primary: {
      base: "bg-primary hover:bg-primary-hover text-on-primary border-0 focus:ring-0",
      // Disabled label color is brand-driven: Kinsa keeps 60% on-primary,
      // Owlet uses full lavender (#D4D4F9 — Figma "On Action - Disabled").
      disabled:
        "bg-primary/40 text-[var(--button-disabled-fg-primary)] border-0 focus:ring-0",
    },
    secondary: {
      base: "bg-transparent border border-primary hover:border-primary-hover hover:bg-primary/10 text-primary focus:ring-0",
      // Disabled secondary is brand-driven: Kinsa renders a ghost (transparent
      // bg + faded border + faded text), Owlet renders a soft lavender fill
      // with no border (Figma "Surface/States/Disabled Secondary CTA").
      disabled:
        "bg-[var(--button-disabled-bg-secondary)] border border-[var(--button-disabled-border-secondary)] text-[var(--button-disabled-fg-secondary)] !border-solid focus:ring-0",
    },
    tertiary: {
      base: "bg-transparent text-primary hover:text-primary-hover border-0 focus:ring-0",
      // Brand-driven: Kinsa keeps its 60%-foreground-muted look; Owlet fades
      // to 40% primary mint (matches "tertiary at 40% opacity" intent).
      disabled:
        "bg-transparent text-[var(--button-disabled-fg-tertiary)] border-0 focus:ring-0",
    },
  };

  // Loading / success state override applies to the PRIMARY variant only.
  // Brand-driven: Kinsa de-saturates to surface (deliberate "loading looks
  // gray" pattern); Owlet stays in primary colors. Secondary and tertiary
  // keep their variant base styling under loading/success.
  const primaryStateOverrideClass =
    "bg-[var(--button-state-bg-primary)] text-[var(--button-state-fg-primary)] border-0";

  // Determine button styling with clear priority order
  const getButtonClassName = () => {
    const currentVariantClasses = buttonVariantClasses[variant];

    // Priority 1: Loading / Success — primary diverges per brand; secondary
    // and tertiary keep their variant base so the button stays in its color
    // scheme under load.
    const hasSpecialState = loading || success;
    if (hasSpecialState) {
      return variant === "primary"
        ? primaryStateOverrideClass
        : currentVariantClasses.base;
    }

    // Priority 2: Handle normal variants based on disabled state
    if (disabled) {
      return currentVariantClasses.disabled;
    }
    return currentVariantClasses.base;
  };

  // brand-button-sizing is a custom @utility (defined in base.css) that
  // applies height + padding-block from --brand-button-* vars.
  const finalButtonClassName = classNames(
    getButtonClassName(),
    "rounded-[var(--button-radius)]",
    "brand-button-sizing",
    className
  );

  return (
    <BaseActionButton
      onPress={onPress}
      eventData={eventData}
      customEventName={customEventName}
      className={finalButtonClassName}
      form={form}
      type={type}
      theme={theme}
      disabled={disabled}
      data-testid={dataTestId}
    >
      <div className="flex flex-row items-center justify-center gap-2 min-w-0 w-full">
        {loading ? (
          <LoadingContent text={text} style={loadingStyle} />
        ) : success ? (
          <SuccessContent text={text} style={successStyle} />
        ) : (
          <>
            {content}
            {leadingIcon}
            <ButtonText as="span" className="truncate">
              {text}
            </ButtonText>
            {trailingIcon}
          </>
        )}
      </div>
    </BaseActionButton>
  );
};

export type { ActionButtonEventData };
export { ActionButton };
export default ActionButton;
